import React, { useRef } from 'react'
import * as Sentry from '@sentry/browser'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { OrderInvalidEnum, useShareMyMemberCouponsMutation } from 'graphQL/dinein/generate/operations'
import {
  buffetSelector,
  setAlertLabelBox,
  setAllowedSharingCoupon,
  setBuffetModal,
  setMemberCoupons,
} from 'store/slices/buffetSlice'
import { SubPageName } from 'enums/buffet'
import { isDevelopment } from 'variables/environment'
import { useTranslation } from 'react-i18next'
import { dineInClient } from 'variables/services'
import ScrollPage from 'components/base-ui/ScrollPage'
import BuffetContainer from 'components/buffet/BuffetContainer'
import { AlertLabelBoxProps } from 'components/base-ui/AlertLabelBox'
import BaseConfirmShareCoupon from 'pages/dine-in/pages/ConfirmShareCoupon'
import { ModalEnum } from 'components/buffet/BuffetModals'

const ConfirmShareCoupon = () => {
  const buffet = useSelector(buffetSelector)
  const dispatch = useDispatch()
  const scrollPageRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [shareMyMemberCoupons, { loading: loadingShareMemberCoupons }] = useShareMyMemberCouponsMutation({
    client: dineInClient,
    onCompleted: ({ shareMyMemberCoupons: { orderInvalids } }) => {
      if (orderInvalids.length) {
        if (orderInvalids.includes(OrderInvalidEnum.InvalidNoCoupon)) {
          dispatch(setBuffetModal({ type: ModalEnum.EmptyCoupons, isProcessing: false }))
          return
        }
        toggleAlertLabel(t('dineIn.invitePage.sharecouponFailed'), 'error')
        return
      }

      dispatch(setAllowedSharingCoupon(true))
      dispatch(setMemberCoupons([]))
      toggleAlertLabel(t('dineIn.invitePage.shareCouponSuccess'))
      navigate(SubPageName.ShareCoupon, { replace: true })
    },
    onError: (error) => {
      !isDevelopment &&
        Sentry.captureException(new Error(`Buffet: Can't share coupon from QR: ${JSON.stringify(error)}`))
    },
  })

  const toggleAlertLabel = (alertText: string, type: AlertLabelBoxProps['type'] = 'success') => {
    dispatch(setAlertLabelBox({ show: true, alertText, type }))
    setTimeout(() => dispatch(setAlertLabelBox({ show: false, alertText: '', type })), 3500)
  }

  const handleConfirmShareCoupons = () => {
    if (loadingShareMemberCoupons) return
    shareMyMemberCoupons({
      variables: {
        orderID: buffet.table.orderID,
      },
    })
  }

  return (
    <BuffetContainer>
      <ScrollPage id="confirm-share-coupon-page" ref={scrollPageRef}>
        <BaseConfirmShareCoupon
          coupons={buffet.memberCoupons}
          onConfirm={handleConfirmShareCoupons}
          onCancle={() => navigate(SubPageName.Home, { replace: true })}
          isProcessing={loadingShareMemberCoupons}
        />
      </ScrollPage>
    </BuffetContainer>
  )
}

export default ConfirmShareCoupon
